// VegasCard — live standings for a Vegas side game (2v2).
//
// Read + one write (presses). Fetches get_vegas_state; renders:
//   • Fixed teams (teams_mode='same'): two team badges A / B with rosters +
//     running $ totals; a "Press this hole" button for the strictly-behind
//     team on an un-scored hole; a "Pressed" badge on pressed holes.
//   • Switch-6 (teams_mode='switch6'): the CURRENT pairing header + per-player
//     running totals (partners rotate every 6 holes / every 3 on a 9-holer).
//   • Per-hole grid: each hole's two team numbers, winner, $ diff, ×2 marker
//     for a natural-eagle double, a carry marker when a Push tie multiplied it,
//     and a Pressed marker.
//
// Compute is server-side (db.js get_vegas_state via the shared _vegas round
// loop). Dollars are shown everywhere (D-278). Refresh via autoRefreshMs poll.
// The press write routes to standalone-game (casual) or event-admin (event)
// exactly like WolfCard, chosen by game.standalone_round_id.

import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { db } from '../lib/db'

// Route a write to the endpoint that owns the game's scope (mirrors WolfCard).
async function callGameAction(game, action, payload) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Please sign in again.')
  const endpoint = game?.standalone_round_id
    ? '/.netlify/functions/standalone-game'
    : '/.netlify/functions/event-admin'
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ action, ...payload }),
  })
  const json = await res.json()
  if (!res.ok) {
    const err = new Error(json.error || `${action} failed`)
    err.code = json.code
    throw err
  }
  return json.data
}

export default function VegasCard({ game_id, viewerCurrentHole, viewerPlayerId, autoRefreshMs = 0 }) {
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState(null)

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const { data } = await db('get_vegas_state', { game_id })
      setState(data)
      setError(data?.error || null)
    } catch (e) {
      setError(e.message || 'Vegas compute failed')
    }
    if (!silent) setLoading(false)
  }, [game_id])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (!autoRefreshMs) return
    const t = setInterval(() => load({ silent: true }), autoRefreshMs)
    return () => clearInterval(t)
  }, [autoRefreshMs, load])

  const pressHole = async () => {
    if (!viewerCurrentHole) return
    setBusy(true)
    setNote(null)
    try {
      await callGameAction(state?.game, 'set_vegas_press', { game_id, hole_number: viewerCurrentHole })
      await load({ silent: true })
    } catch (e) {
      setNote(e.message || 'Could not press this hole')
    }
    setBusy(false)
  }

  if (loading && !state) return <p className="text-muted text-sm">Loading Vegas…</p>
  if (error === 'vegas_needs_team_assignment') return <p style={{ color: 'var(--red)' }}>Assign all four players to Team A or Team B.</p>
  if (error === 'vegas_needs_two_teams_of_two') return <p style={{ color: 'var(--red)' }}>Vegas needs two teams of exactly two players.</p>
  if (error === 'vegas_needs_four_players') return <p style={{ color: 'var(--red)' }}>Vegas needs exactly four players.</p>
  if (error && !state?.sides) return <p style={{ color: 'var(--red)' }}>{error}</p>
  if (!state) return null

  const {
    participants = [], sides, holes = [], totals = {}, unit_value = 0, flip_enabled,
    teams_mode = 'same', ties_mode = 'wash', allow_press = false, presses = {},
    down_side = null, active_carry = 0,
  } = state
  const isSwitch6 = teams_mode === 'switch6'
  const nameFor = (pid) => participants.find(p => p.player_id === pid)?.name || (pid ? pid.slice(0, 6) : '—')

  // Press eligibility (fixed teams only): current hole not yet settled, not
  // already pressed, and there is a strictly-behind team. Any player may tap.
  const currentSettled = viewerCurrentHole
    ? holes.some(h => h.course_hole === viewerCurrentHole)
    : true
  const alreadyPressed = viewerCurrentHole ? !!presses[String(viewerCurrentHole)] : false
  const canPress = allow_press && !isSwitch6 && !!viewerCurrentHole
    && !!down_side && !currentSettled && !alreadyPressed && !busy

  return (
    <div>
      {/* Fixed-teams badges, or switch-6 current pairing */}
      {isSwitch6 ? (
        <div style={{
          padding: 10, borderRadius: 6, marginBottom: 12,
          background: 'var(--green-dark)', border: '1px solid var(--green-mid)',
        }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            Current pairing (partners rotate)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'center', fontSize: '0.8rem' }}>
            <div style={{ textAlign: 'right', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(sides?.A || []).length === 0
                ? <span>—</span>
                : (sides?.A || []).map((pid, i) => (<span key={pid || i}>{nameFor(pid)}</span>))}
            </div>
            <span style={{ color: 'var(--gray-400)', fontSize: '0.7rem' }}>vs</span>
            <div style={{ fontWeight: 600, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(sides?.B || []).length === 0
                ? <span>—</span>
                : (sides?.B || []).map((pid, i) => (<span key={pid || i}>{nameFor(pid)}</span>))}
            </div>
          </div>
          {/* Per-player running totals */}
          <div style={{ display: 'grid', gap: 3, marginTop: 8, borderTop: '1px solid var(--green-mid)', paddingTop: 6 }}>
            {participants.map(p => {
              const u = Number(totals[p.player_id]?.units) || 0
              return (
                <div key={p.player_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                  <span>{p.name}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', color: u > 0 ? 'var(--blue-birdie)' : u < 0 ? 'var(--red)' : 'var(--gray-400)' }}>
                    {unit_value > 0 ? `${u >= 0 ? '+' : ''}$${u.toFixed(2)}` : (u >= 0 ? `+${u}` : `${u}`)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        sides && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <TeamBadge side="A" ids={sides.A || []} nameFor={nameFor} totals={totals} down={down_side === 'A'} unitValue={unit_value} />
            <TeamBadge side="B" ids={sides.B || []} nameFor={nameFor} totals={totals} down={down_side === 'B'} unitValue={unit_value} />
          </div>
        )
      )}

      <p style={{ fontSize: '0.7rem', color: 'var(--gray-400)', margin: '0 0 8px' }}>
        {unit_value > 0 ? `$${unit_value}/point` : 'Bragging rights'}
        {' · '}
        {flip_enabled ? 'Flip ON' : 'Flip OFF'}
        {' · '}
        {ties_mode === 'push' ? 'Push ties' : 'Wash ties'}
        {isSwitch6 ? ' · Switch teams' : ''}
        {allow_press ? ' · Press on' : ''}
      </p>

      {/* Active carry indicator (Push) */}
      {ties_mode === 'push' && active_carry > 0 && (
        <p style={{ fontSize: '0.72rem', color: 'var(--gold)', margin: '0 0 8px', fontWeight: 600 }}>
          Carry ×{active_carry + 1} pending — the next decided hole pays ×{active_carry + 1}.
        </p>
      )}

      {/* Press button (fixed teams, behind team, un-scored hole) */}
      {canPress && (
        <button
          onClick={pressHole}
          disabled={busy}
          style={{
            width: '100%', padding: '10px 12px', marginBottom: 8, borderRadius: 6,
            background: 'var(--gold)', color: 'var(--green-deep)',
            border: 'none', fontWeight: 700, fontSize: '0.82rem',
            cursor: busy ? 'default' : 'pointer',
          }}
        >
          Team {down_side} is down — Press hole {viewerCurrentHole} (×2)
        </button>
      )}
      {allow_press && !isSwitch6 && !!viewerCurrentHole && alreadyPressed && !currentSettled && (
        <p style={{ fontSize: '0.72rem', color: 'var(--gold)', margin: '0 0 8px', fontWeight: 600 }}>
          Hole {viewerCurrentHole} is pressed — points on this hole double.
        </p>
      )}

      {note && (
        <div role="alert" style={{
          margin: '0 0 8px', padding: '8px 10px', borderRadius: 6,
          background: 'rgba(229, 115, 115, 0.15)', border: '1px solid var(--red)',
          color: 'var(--red)', fontSize: '0.8rem', fontWeight: 600,
        }}>⚠️ {note}</div>
      )}

      {/* Per-hole results */}
      {holes.length === 0 ? (
        <p style={{ fontSize: '0.8rem', color: 'var(--gray-400)', margin: 0 }}>
          No completed holes yet. Each hole settles once all four players post a score.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 4 }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '38px 1fr 1fr auto', gap: 6,
            fontSize: '0.65rem', color: 'var(--gray-400)', textTransform: 'uppercase',
            letterSpacing: '0.04em', padding: '0 6px',
          }}>
            <span>Hole</span>
            <span style={{ textAlign: 'center' }}>Team A</span>
            <span style={{ textAlign: 'center' }}>Team B</span>
            <span style={{ textAlign: 'right' }}>$</span>
          </div>
          {holes.map(h => {
            const aWin = h.winner_side === 'A'
            const bWin = h.winner_side === 'B'
            return (
              <div
                key={h.hole}
                style={{
                  display: 'grid', gridTemplateColumns: '38px 1fr 1fr auto', gap: 6,
                  alignItems: 'center', padding: '6px', borderRadius: 6,
                  background: 'var(--green-dark)', border: '1px solid var(--green-mid)',
                  fontSize: '0.8rem',
                }}
              >
                <span style={{ color: 'var(--gray-400)', fontFamily: 'var(--font-mono)' }}>
                  {h.hole}{h.par ? <span style={{ fontSize: '0.6rem', color: 'var(--gray-500)' }}> ·{h.par}</span> : null}
                  {h.pressed ? <span title="Pressed — points doubled" style={{ display: 'block', fontSize: '0.55rem', color: 'var(--gold)', letterSpacing: '0.03em' }}>PRESSED</span> : null}
                </span>
                <span style={{
                  textAlign: 'center', fontFamily: 'var(--font-mono)', fontWeight: 700,
                  color: aWin ? 'var(--gold)' : 'var(--cream)',
                }}>{h.team_a_number}</span>
                <span style={{
                  textAlign: 'center', fontFamily: 'var(--font-mono)', fontWeight: 700,
                  color: bWin ? 'var(--gold)' : 'var(--cream)',
                }}>{h.team_b_number}</span>
                <span style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>
                  {h.winner_side
                    ? <span style={{ color: 'var(--blue-birdie)' }}>
                        {h.winner_side}{unit_value > 0 ? ` +$${h.hole_wager}` : ''}
                        {h.doubled ? <span title="Natural eagle doubled the hole" style={{ color: 'var(--gold)', marginLeft: 4 }}>×2</span> : null}
                        {h.carry_mult > 1 ? <span title="Push carry" style={{ color: 'var(--gold)', marginLeft: 4 }}>↺×{h.carry_mult}</span> : null}
                      </span>
                    : <span style={{ color: 'var(--gray-500)' }}>push</span>}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function TeamBadge({ side, ids, nameFor, totals, down, unitValue }) {
  const units = ids.reduce((s, pid) => s + (Number(totals[pid]?.units) || 0), 0)
  const color = side === 'A' ? 'var(--gold)' : 'var(--green-mid)'
  const fg = side === 'A' ? 'var(--green-deep)' : 'var(--cream)'
  return (
    <div style={{
      padding: 10, borderRadius: 6,
      background: 'var(--green-dark)',
      border: `1px solid ${color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{
          padding: '2px 8px', borderRadius: 999,
          background: color, color: fg,
          fontSize: '0.7rem', fontWeight: 700,
        }}>Team {side}{down ? ' · down' : ''}</span>
        {units !== 0 && (
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.78rem', fontWeight: 700,
            color: units > 0 ? 'var(--blue-birdie)' : 'var(--red)',
          }}>{unitValue > 0 ? `${units > 0 ? '+' : ''}$${units.toFixed(2)}` : `${units > 0 ? '+' : ''}${units}`}</span>
        )}
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--cream)', lineHeight: 1.4, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {ids.length === 0
          ? <span>—</span>
          : ids.map((pid, i) => (<span key={pid || i}>{nameFor(pid)}</span>))}
      </div>
    </div>
  )
}
